import { createContext, useContext, useMemo } from 'react';
import { LOCAL_STORAGE_NAMES } from '../constants/constants';
import { useFetchSystemInfo } from '../hooks/useFetchSystemInfo';
import { ISystemInfoResponseDto } from '../interface/system';
import { useUser } from './UserContext';

interface ISystemInfoContext {
  systemParams?: ISystemInfoResponseDto;
  isSystemInfoLoading: boolean;
}

const SystemInfoContext = createContext<ISystemInfoContext | undefined>(undefined);

const getCurrentOrganizationId = () => {
  const currentOrganisation = localStorage.getItem(LOCAL_STORAGE_NAMES.currentOrganisation);
  if (!currentOrganisation) {
    return undefined;
  }

  try {
    return JSON.parse(currentOrganisation)?.organizationId as string | undefined;
  } catch {
    return undefined;
  }
};

export const SystemInfoProvider = ({ children }: { children: React.ReactNode }) => {
  const { user } = useUser();
  const organizationId = getCurrentOrganizationId();
  const shouldFetchSystemInfo = Boolean(user?.id && organizationId);

  const { data, isLoading } = useFetchSystemInfo({ enabled: shouldFetchSystemInfo });

  const value = useMemo(
    () => ({
      systemParams: shouldFetchSystemInfo ? data : undefined,
      isSystemInfoLoading: shouldFetchSystemInfo ? isLoading : false,
    }),
    [data, isLoading, shouldFetchSystemInfo],
  );

  return <SystemInfoContext.Provider value={value}>{children}</SystemInfoContext.Provider>;
};

export const useSystemInfo = () => {
  const context = useContext(SystemInfoContext);
  if (!context) {
    throw new Error('useSystemInfo must be used within a SystemInfoProvider');
  }

  return context;
};
